/**
 * Installs page (099) — display strings and the CSV export (spec §6.8, §6.13). No DOM.
 * Every value here came off someone's phone, so nothing is ever treated as HTML or a formula.
 */
import { buildLabel, stillOpen, paramValue, focusItem, DEFAULT_FILTERS, dialogFilterCount } from './installsModel';

const pad = (n, w = 2) => String(n).padStart(w, '0');

/** `+NNNms` · `+S.Ss` · `+Mm SSs` · `+Hh MMm`, clamped at zero. */
export function fmtDelta(ms) {
  const d = Math.max(0, Number(ms) || 0);
  if (d < 1000) return `+${Math.round(d)}ms`;
  if (d < 60000) return `+${(Math.floor(d / 100) / 10).toFixed(1)}s`;
  if (d < 3600000) return `+${Math.floor(d / 60000)}m ${pad(Math.floor((d % 60000) / 1000))}s`;
  return `+${Math.floor(d / 3600000)}h ${pad(Math.floor((d % 3600000) / 60000))}m`;
}

/** 'YYYY-MM-DD HH:MM:SS' in UTC. */
export const isoUtc = (ms) => new Date(ms).toISOString().slice(0, 19).replace('T', ' ');

const stamp = (ms, timeZone) => new Intl.DateTimeFormat('en-GB', {
  day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false, timeZone,
}).format(new Date(ms));

/** Property time (UTC), and local time only when it reads differently. */
export function timeCell(ms, localZone) {
  const utc = stamp(ms, 'UTC');
  let local = null;
  try {
    const l = stamp(ms, localZone);
    if (l !== utc) local = l;
  } catch { /* an unknown zone: show UTC only */ }
  return { utc, local };
}

/** '21 Sept' for a 'YYYY-MM-DD' day. */
export const shortDay = (iso) => new Intl.DateTimeFormat('en-GB', { day: 'numeric', month: 'short', timeZone: 'UTC' })
  .format(new Date(`${iso}T00:00:00Z`));

export const rangeLabel = (from, to) => (from === to || !to ? shortDay(from) : `${shortDay(from)} → ${shortDay(to)}`);

export const browserZone = () => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  } catch {
    return 'UTC';
  }
};

/* ── CSV ── */

export const CSV_COLUMNS = [
  'first_open_utc', 'user_pseudo_id', 'device_id', 'user_id', 'build', 'platform', 'app_version',
  'device_model', 'os_version', 'country',
  'opened_app_after_s', 'install_event_after_s', 'sign_in_started_after_s', 'signed_in_after_s',
  'sign_in_kind', 'sign_in_error_after_s', 'sign_in_cancel_after_s',
  'pending', 'install_source', 'previous_first_open_count', 'ga_session_id',
];

/** Defuse spreadsheet formulas (a leading = + - @ tab CR gets a '), then quote if needed. */
export function csvCell(value) {
  if (value === null || value === undefined) return '';
  let v = String(value);
  if (/^[=+\-@\t\r]/.test(v)) v = `'${v}`;
  if (/[",\n\r]/.test(v)) v = `"${v.replace(/"/g, '""')}"`;
  return v;
}

const afterS = (anchor, t) => (t === null || t === undefined ? '' : (Math.max(0, t - anchor) / 1000).toFixed(1));

export function csvRow(r) {
  const f = r.followUps || {};
  return [
    isoUtc(r.timestampMillis), r.userPseudoId, paramValue(r, 'device_id') ?? r.installDeviceId, r.signedInUserId,
    buildLabel(r.target), r.platform, r.appVersion, r.deviceModel, r.osVersion, r.country,
    afterS(r.timestampMillis, f.opened), afterS(r.timestampMillis, f.install),
    afterS(r.timestampMillis, f.signInStart), afterS(r.timestampMillis, f.signedIn),
    r.signInKind, afterS(r.timestampMillis, r.signInErrorMillis), afterS(r.timestampMillis, r.signInCancelMillis),
    stillOpen(r) ? 1 : 0, r.installSource, paramValue(r, 'previous_first_open_count'), r.sessionId,
  ];
}

/** UTF-8 with BOM, CRLF line ends — what spreadsheets open without asking. */
export function buildCsv(rows) {
  const lines = [CSV_COLUMNS.join(','), ...rows.map((r) => csvRow(r).map(csvCell).join(','))];
  return `﻿${lines.join('\r\n')}\r\n`;
}

/** installs_<from>_<to>[_<focus>][_filtered].csv */
export function csvFilename(meta, filters) {
  const parts = ['installs', meta?.from, meta?.to];
  if (filters.focus !== DEFAULT_FILTERS.focus) parts.push(focusItem(filters.focus).key);
  if (dialogFilterCount(filters) || filters.text.trim()) parts.push('filtered');
  return `${parts.join('_')}.csv`;
}
